/**
 * MetricsPollerService - Centralized Metrics Polling with Throttling
 *
 * This singleton service consolidates all monitoring metrics polling into one place:
 * - Fetches /api/monitoring/metrics and /api/system/metrics in one tick
 * - Stores results in a shared Zustand store (useMetricsStore)
 * - Default 60s interval with exponential backoff on 429
 * - Pauses when tab is hidden (document.visibilityState)
 * - No component-level polling - widgets subscribe to store only
 *
 * Phase 3C+: Eliminates 429 rate limiting on monitoring endpoints
 */

import { create } from 'zustand';

// Types for monitoring metrics (from /api/monitoring/metrics)
export interface MonitoringMetrics {
    timestamp: number;
    uptimeSeconds: number;
    cpu: {
        count: number | null;
        loadAverage: {
            '1m': number | null;
            '5m': number | null;
            '15m': number | null;
        };
    };
    memory: {
        totalBytes: number | null | undefined;
        availableBytes: number | null | undefined;
        freeBytes: number | null | undefined;
        usedBytes: number | null | undefined;
        usagePercent: number | null | undefined;
        totalMegabytes: number | null | undefined;
        availableMegabytes: number | null | undefined;
        usedMegabytes: number | null | undefined;
    };
    process: {
        pid: number | null;
        rssBytes: number | null | undefined;
        rssMegabytes: number | null | undefined;
        cpuTimes: {
            user: number | null | undefined;
            system: number | null | undefined;
        };
    };
    environment: {
        hostname: string | null;
        platform: string | null;
        platformVersion: string | null;
    };
}

// Types for system metrics (from /api/system/metrics)
export interface SystemMetrics {
    workers?: number;
    queueDepth?: number;
    activeMissions?: number;
    completedMissions?: number;
    failedMissions?: number;
    avgExecutionTime?: number;
    uptime?: number;
    memoryUsage?: number;
    cpuUsage?: number;
}

// Combined metrics store state
export interface MetricsStoreState {
    // Monitoring metrics (/api/monitoring/metrics)
    monitoringMetrics: MonitoringMetrics | null;
    monitoringLoading: boolean;
    monitoringError: string | null;

    // System metrics (/api/system/metrics)
    systemMetrics: SystemMetrics | null;
    systemLoading: boolean;
    systemError: string | null;

    // Rate limit state
    isRateLimited: boolean;
    rateLimitRetryAt: number | null; // timestamp when retry will happen
    currentBackoffMs: number;

    // Timestamps
    lastUpdated: number | null;
    lastFetchAttempt: number | null;

    // Actions
    setMonitoringMetrics: (metrics: MonitoringMetrics | null, error?: string | null) => void;
    setSystemMetrics: (metrics: SystemMetrics | null, error?: string | null) => void;
    setRateLimited: (limited: boolean, retryAt?: number | null, backoffMs?: number) => void;
    setLastUpdated: (timestamp: number) => void;
}

// Zustand store for metrics
export const useMetricsStore = create<MetricsStoreState>(set => ({
    monitoringMetrics: null,
    monitoringLoading: false,
    monitoringError: null,

    systemMetrics: null,
    systemLoading: false,
    systemError: null,

    isRateLimited: false,
    rateLimitRetryAt: null,
    currentBackoffMs: 60000, // Start at 60s

    lastUpdated: null,
    lastFetchAttempt: null,

    setMonitoringMetrics: (metrics, error = null) =>
        set({
            monitoringMetrics: metrics,
            monitoringLoading: false,
            monitoringError: error,
        }),

    setSystemMetrics: (metrics, error = null) =>
        set({
            systemMetrics: metrics,
            systemLoading: false,
            systemError: error,
        }),

    setRateLimited: (limited, retryAt = null, backoffMs = 60000) =>
        set({
            isRateLimited: limited,
            rateLimitRetryAt: retryAt,
            currentBackoffMs: backoffMs,
        }),

    setLastUpdated: timestamp =>
        set({
            lastUpdated: timestamp,
            lastFetchAttempt: timestamp,
        }),
}));

// Polling configuration
const DEFAULT_POLL_INTERVAL_MS = 60000; // 60 seconds
const MAX_BACKOFF_MS = 300000; // 5 minutes (300 seconds)
const BACKOFF_MULTIPLIER = 2;

/**
 * MetricsPollerService - Singleton service for centralized metrics polling
 */
class MetricsPollerService {
    private static instance: MetricsPollerService | null = null;

    private intervalId: ReturnType<typeof setTimeout> | null = null;
    private isPolling = false;
    private currentBackoffMs = DEFAULT_POLL_INTERVAL_MS;
    private isVisible = true;
    private baseUrl: string = '';
    private started = false;

    private constructor() {
        // Set up visibility change listener
        if (typeof document !== 'undefined') {
            this.isVisible = document.visibilityState === 'visible';
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    static getInstance(): MetricsPollerService {
        if (!MetricsPollerService.instance) {
            MetricsPollerService.instance = new MetricsPollerService();
        }
        return MetricsPollerService.instance;
    }

    /**
     * Start the metrics poller with the given base URL
     */
    start(baseUrl?: string): void {
        if (this.started) {
            console.debug('[MetricsPoller] Already started, skipping');
            return;
        }

        const env =
            typeof import.meta !== 'undefined'
                ? (import.meta.env as Record<string, string | undefined>)
                : {};
        this.baseUrl = baseUrl || env.VITE_HIVECORE_API_URL || env.VITE_BACKEND_URL || '';

        console.debug('[MetricsPoller] Starting with baseUrl:', this.baseUrl || '(relative)');

        this.started = true;
        this.currentBackoffMs = DEFAULT_POLL_INTERVAL_MS;

        // Initial fetch
        this.poll();

        // Schedule next poll
        this.scheduleNextPoll();
    }

    /**
     * Stop the metrics poller
     */
    stop(): void {
        console.debug('[MetricsPoller] Stopping');
        this.started = false;

        if (this.intervalId) {
            clearTimeout(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * Force an immediate refresh (bypasses visibility check)
     */
    forceRefresh(): void {
        if (!this.started) {
            console.debug('[MetricsPoller] Not started, cannot force refresh');
            return;
        }

        console.debug('[MetricsPoller] Force refresh requested');
        this.poll();
    }

    /**
     * Handle visibility change - pause when hidden, resume when visible
     */
    private handleVisibilityChange = (): void => {
        const wasVisible = this.isVisible;
        this.isVisible = document.visibilityState === 'visible';

        console.debug('[MetricsPoller] Visibility changed:', {
            wasVisible,
            isVisible: this.isVisible,
        });

        if (!wasVisible && this.isVisible && this.started) {
            // Tab became visible - check if we need to refresh
            const store = useMetricsStore.getState();
            const lastUpdated = store.lastUpdated;
            const now = Date.now();

            // If last update was more than 60s ago, fetch immediately
            if (!lastUpdated || now - lastUpdated > DEFAULT_POLL_INTERVAL_MS) {
                console.debug('[MetricsPoller] Tab visible, data stale, fetching immediately');
                this.poll();
            }

            // Resume polling schedule
            this.scheduleNextPoll();
        } else if (wasVisible && !this.isVisible) {
            // Tab became hidden - stop polling
            console.debug('[MetricsPoller] Tab hidden, pausing polling');
            if (this.intervalId) {
                clearTimeout(this.intervalId);
                this.intervalId = null;
            }
        }
    };

    /**
     * Schedule the next poll based on current backoff
     */
    private scheduleNextPoll(): void {
        if (this.intervalId) {
            clearTimeout(this.intervalId);
        }

        if (!this.started || !this.isVisible) {
            return;
        }

        this.intervalId = setTimeout(() => {
            this.poll();
            this.scheduleNextPoll();
        }, this.currentBackoffMs);

        console.debug('[MetricsPoller] Next poll scheduled in', this.currentBackoffMs, 'ms');
    }

    /**
     * Perform the actual metrics fetch
     */
    private async poll(): Promise<void> {
        if (this.isPolling) {
            console.debug('[MetricsPoller] Already polling, skipping');
            return;
        }

        this.isPolling = true;
        const store = useMetricsStore.getState();

        try {
            // Fetch both endpoints in parallel
            const [monitoringResult, systemResult] = await Promise.allSettled([
                this.fetchMonitoringMetrics(),
                this.fetchSystemMetrics(),
            ]);

            let hadRateLimit = false;

            // Process monitoring metrics result
            if (monitoringResult.status === 'fulfilled') {
                if (monitoringResult.value.rateLimited) {
                    hadRateLimit = true;
                } else if (monitoringResult.value.data) {
                    store.setMonitoringMetrics(monitoringResult.value.data);
                } else if (monitoringResult.value.error) {
                    store.setMonitoringMetrics(null, monitoringResult.value.error);
                }
            } else {
                store.setMonitoringMetrics(
                    null,
                    monitoringResult.reason?.message || 'Unknown error'
                );
            }

            // Process system metrics result
            if (systemResult.status === 'fulfilled') {
                if (systemResult.value.rateLimited) {
                    hadRateLimit = true;
                } else if (systemResult.value.data) {
                    store.setSystemMetrics(systemResult.value.data);
                } else if (systemResult.value.error) {
                    store.setSystemMetrics(null, systemResult.value.error);
                }
            } else {
                store.setSystemMetrics(null, systemResult.reason?.message || 'Unknown error');
            }

            // Handle rate limiting
            if (hadRateLimit) {
                this.handleRateLimit();
            } else {
                // Success - reset backoff to default
                this.currentBackoffMs = DEFAULT_POLL_INTERVAL_MS;
                store.setRateLimited(false);
            }

            store.setLastUpdated(Date.now());
        } catch (error) {
            console.error('[MetricsPoller] Unexpected error during poll:', error);
        } finally {
            this.isPolling = false;
        }
    }

    /**
     * Fetch monitoring metrics from /api/monitoring/metrics
     */
    private async fetchMonitoringMetrics(): Promise<{
        data?: MonitoringMetrics;
        error?: string;
        rateLimited?: boolean;
    }> {
        try {
            const url = this.baseUrl
                ? `${this.baseUrl.replace(/\/+$/, '')}/api/monitoring/metrics`
                : '/api/monitoring/metrics';

            const response = await fetch(url, { credentials: 'include' });

            if (response.status === 429) {
                return { rateLimited: true };
            }

            if (!response.ok) {
                return { error: `HTTP ${response.status}: ${response.statusText}` };
            }

            const data = await response.json();
            return { data };
        } catch (error) {
            return { error: error instanceof Error ? error.message : 'Network error' };
        }
    }

    /**
     * Fetch system metrics from /api/system/metrics
     */
    private async fetchSystemMetrics(): Promise<{
        data?: SystemMetrics;
        error?: string;
        rateLimited?: boolean;
    }> {
        try {
            const url = this.baseUrl
                ? `${this.baseUrl.replace(/\/+$/, '')}/api/system/metrics`
                : '/api/system/metrics';

            const response = await fetch(url, { credentials: 'include' });

            if (response.status === 429) {
                return { rateLimited: true };
            }

            if (response.status === 403) {
                // System metrics only available in staging
                return { error: 'System metrics only available in staging environment' };
            }

            if (!response.ok) {
                return { error: `HTTP ${response.status}: ${response.statusText}` };
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                return { error: 'Non-JSON response received' };
            }

            const data = await response.json();
            if (!data.success || !data.metrics) {
                return { error: 'Invalid response format' };
            }

            return { data: data.metrics };
        } catch (error) {
            return { error: error instanceof Error ? error.message : 'Network error' };
        }
    }

    /**
     * Handle rate limit - increase backoff exponentially
     */
    private handleRateLimit(): void {
        const store = useMetricsStore.getState();

        // Increase backoff exponentially, capped at MAX_BACKOFF_MS
        this.currentBackoffMs = Math.min(
            this.currentBackoffMs * BACKOFF_MULTIPLIER,
            MAX_BACKOFF_MS
        );

        const retryAt = Date.now() + this.currentBackoffMs;

        console.warn(
            '[MetricsPoller] Rate limited (429), backing off to',
            this.currentBackoffMs / 1000,
            'seconds'
        );

        store.setRateLimited(true, retryAt, this.currentBackoffMs);
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.stop();

        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }

        MetricsPollerService.instance = null;
    }
}

// Export singleton instance getter
export const getMetricsPoller = (): MetricsPollerService => MetricsPollerService.getInstance();

// Export for testing
export { MetricsPollerService };
